import { first, contains, isEmpty } from 'underscore';
import React, { useContext, useCallback } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { Label, Stack } from '@fluentui/react';
import { UrlInputFluent, withNoProtocol } from '@bingads-webui-campaign-react/url-input-fluent';
import { UrlInputFluent as UrlInputFluentV2, withNoProtocol as withNoProtocolV2 } from '@bingads-webui-campaign-react/url-input-fluent-v2';
import { HelpPopupInline } from '@bingads-webui-react/help-popup';
import { withError, withCustomValidation } from '@bingads-webui-react/validation';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { ActivityContext } from '@bingads-webui-react/hoc-with-signal';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { Input } from '@fluentui/react-components';

const UrlInputFluentWithNoProtocol = withNoProtocol(UrlInputFluent);
const UrlInputFluentV2WithNoProtocol = withNoProtocolV2(UrlInputFluentV2);

const getStyles = () =>
  ({
    websiteTitle: {
      '& .title': {
        fontSize: '16px',
        fontWeight: '600',
      },
    },
    websiteContent: {
      width: '380px',
      '& .url-field': {
        width: '100%',
      },
    },
  });

export const emptyUrls = ['http://', 'https://'];

export const ValidatedWebsiteTitle = () => {
  const classes = useDefaultStyles(getStyles);
  const i18n = useContext(I18nContext);

  return (
    <div className={classnames('website-section', classes.websiteTitle)}>
      <Label>
        <Stack horizontal verticalAlign="center" >
          <div className="title">{i18n.getString(_TL_('Your website'))}</div>
          <HelpPopupInline
            topic="pop_BA_SearchRevamp_Website"
            aria-label={i18n.getString(_TL_('Click to learn more about website'))}
          />
        </Stack>
      </Label>
    </div>
  );
};

const ValidatedNoProtocolWebsiteContent = ({
  validationProps, updateValidation, validateOnRender, enableSpecialCharactersValidation, useFluentV2, isEmptyValid, disabled,
}) => {
  const { data: websites } = validationProps;
  const website = first(websites);

  const classes = useDefaultStyles(getStyles);
  const i18n = useContext(I18nContext);
  const activity = useContext(ActivityContext);

  const onChange = useCallback((errors, value) => {
    if (isEmptyValid && (!value || contains(emptyUrls, value))) {
      // website is optional
      updateValidation([], { websites: [value] });
    } else {
      updateValidation(errors, { websites: [value] }, (!isEmpty(errors) && !!website));
    }

    if (activity) {
      activity.signal(`onWebsiteChanged - ${JSON.stringify(value)}`);
    }
  }, [activity, updateValidation, isEmptyValid, website]);

  return (
    <div className={classnames('website-section', classes.websiteContent)}>
      {
        disabled && (
          <Input
            value={website}
            className="url-field"
            aria-label={i18n.getString(_TL_('Website'))}
            placeholder={i18n.getString(_TL_('Enter your website'))}
            disabled
          />
        )
      }
      {
        !disabled && (
          useFluentV2 ? (
            <UrlInputFluentV2WithNoProtocol
              url={website}
              onChange={onChange}
              i18n={i18n}
              dropdownOptionAriaLabel={i18n.getString(_TL_('Website options'))}
              propertyKey="website"
              validateOnRender={validateOnRender}
              containerClassName="website"
              textFieldAriaLabel={i18n.getString(_TL_('Website'))}
              enableSpecialCharactersValidation={enableSpecialCharactersValidation}
            />) : (
              <UrlInputFluentWithNoProtocol
                url={website}
                onChange={onChange}
                i18n={i18n}
                dropdownOptionAriaLabel={i18n.getString(_TL_('Website options'))}
                propertyKey="website"
                validateOnRender={validateOnRender}
                containerClassName="website"
                textFieldAriaLabel={i18n.getString(_TL_('Website'))}
                enableSpecialCharactersValidation={enableSpecialCharactersValidation}
              />
            )
        )
      }
    </div>
  );
};

ValidatedNoProtocolWebsiteContent.propTypes = {
  validationProps: PropTypes.shape({
    data: PropTypes.shape([]),
  }).isRequired,
  updateValidation: PropTypes.func.isRequired,
  validateOnRender: PropTypes.bool,
  disabled: PropTypes.bool,
  enableSpecialCharactersValidation: PropTypes.bool,
  useFluentV2: PropTypes.bool,
  isEmptyValid: PropTypes.bool,
};

ValidatedNoProtocolWebsiteContent.defaultProps = {
  validateOnRender: false,
  disabled: false,
  enableSpecialCharactersValidation: false,
  useFluentV2: false,
  isEmptyValid: false,
};

export const ValidatedNoProtocolWebsite = withError(withCustomValidation(ValidatedNoProtocolWebsiteContent));
